import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import * as _ from 'lodash';
import {DatePipe} from '@angular/common';
import {SelectItem} from 'primeng/api';
import {Table} from 'primeng/table';
import {BaseClientComponent} from '../base-client.component';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {ClfLedgerRow} from '../../models/clf-ledger';
import {UserLogged} from '../../models/userLogged';
import {EtreeService} from '../../services/etree.service';

@Component({
  selector: 'app-clf-ledger',
  templateUrl: './clf-ledger.component.html',
  styleUrls: ['./clf-ledger.component.css']
})
export class ClfLedgerComponent extends BaseClientComponent implements OnInit, OnDestroy {
  @ViewChild('dataTableClfLedger') dataTableClfLedger: Table;

  currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
  };
  loggedInUser: UserLogged = null;
  isMckayUser = false;
  clfLedgerRows: Array<ClfLedgerRow> = [];
  filteredClfLedgerRows: Array<ClfLedgerRow> = [];
  crossConnectIdOptions: SelectItem[] = [];
  loading = true;
  cols: any[];

  userChoicesFiltersDefaultClfLedger = {
    eTreeId: null,
    crossConnectId: null,
    serviceType: null,
    connectionDateRange: null as [string, string] | null,
    showHistorical: false,
  };
  userChoicesActiveFiltersClfLedger = _.clone(this.userChoicesFiltersDefaultClfLedger);
  constructor(
    public route: ActivatedRoute,
    public etreeService: EtreeService,
    public datePipe: DatePipe,
    public authenticationService: AuthenticationService,
    protected utilsService: UtilsService,
  ) {
    super();

    this.cols = [
      {field: 'internalUseOnly', header: 'Internal Use Only', sortDisabled: false, class: '', mckayOnly: true},
      {field: 'serviceType', header: 'Service Type', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'priorScheduleRef', header: 'Prior Schedule Ref', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'eTreeId', header: 'E-Tree ID', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'crossConnectId', header: 'Cross Connect ID', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'monthlyFee', header: 'Monthly Fee', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'bandwidthMbps', header: 'Bandwidth (Mbps)', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'injectionDemarcationPoint', header: 'Injection Demarcation Point', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'receptionDemarcationPoint', header: 'Reception Demarcation Point', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'connectionDate', header: 'Connection Date', sortDisabled: false, class: '', mckayOnly: false},
      {field: 'removalDate', header: 'Removal Date', sortDisabled: false, class: '', mckayOnly: false},
    ];
  }

  get visibleCols(): any[] {
    return this.cols.filter(col => !col.mckayOnly || this.isMckayUser);
  }

  ngOnInit() {
    this.loggedInUser = this.authenticationService.getUserCurrent();
    this.isMckayUser = this.loggedInUser?.permissions?.isMckayUser ?? false;
    this.userChoicesActiveFiltersClfLedger = {
      ...this.userChoicesFiltersDefaultClfLedger,
      ...this.utilsService.loadUserPref('clfLedgerFilter', {}),
    };
    this.loadEtreeLedgers();
  }

  public loadEtreeLedgers() {
    this.utilsService.lockUi();
    this.etreeService.getEtreeLedgers(this.userChoicesActiveFiltersClfLedger.showHistorical).subscribe(res => {
      this.clfLedgerRows = res || [];
      this.buildCrossConnectIdOptions();
      this.dropMissingCrossConnectIds();
      this.applyFiltersOnDataTableItem();
      if (this.dataTableClfLedger) {
        this.dataTableClfLedger.first = 0;
      }
      this.utilsService.unlockUi();
      this.loading = false;
    }, () => {
      this.clfLedgerRows = [];
      this.applyFiltersOnDataTableItem();
      this.utilsService.unlockUi();
      this.loading = false;
    });
  }

  public onHistoricalChange() {
    this.loadEtreeLedgers();
  }

  public onClickedResetFilter() {
    const wasShowingHistorical = this.userChoicesActiveFiltersClfLedger.showHistorical;
    this.userChoicesActiveFiltersClfLedger = _.clone(this.userChoicesFiltersDefaultClfLedger);
    if (wasShowingHistorical) {
      this.loadEtreeLedgers();
      return;
    }
    this.applyFiltersOnDataTableItem();
  }

  public filterAndReset() {
    this.applyFiltersOnDataTableItem();
    this.dataTableClfLedger.first = 0;
  }

  private buildCrossConnectIdOptions() {
    this.crossConnectIdOptions = _.chain(this.clfLedgerRows)
      .map(row => row.crossConnectId)
      .filter(value => !!value)
      .uniq()
      .sortBy()
      .map(value => ({label: value, value}))
      .value();
  }

  private dropMissingCrossConnectIds() {
    const selectedIds = this.userChoicesActiveFiltersClfLedger.crossConnectId;
    if (!selectedIds || selectedIds.length === 0) {
      return;
    }
    const availableIds = this.crossConnectIdOptions.map(option => option.value);
    const keptIds = selectedIds.filter(id => availableIds.includes(id));
    if (keptIds.length !== selectedIds.length) {
      this.userChoicesActiveFiltersClfLedger.crossConnectId = keptIds.length > 0 ? keptIds : null;
    }
  }

  public applyFiltersOnDataTableItem() {
    this.utilsService.saveUserData('clfLedgerFilter', this.userChoicesActiveFiltersClfLedger);
    this.filteredClfLedgerRows = this.clfLedgerRows.filter(row => this.filter(row));
  }

  filter(row: ClfLedgerRow) {
    const range = this.userChoicesActiveFiltersClfLedger.connectionDateRange;
    return this.matches(row.eTreeId, this.userChoicesActiveFiltersClfLedger.eTreeId)
      && this.matchesAny(row.crossConnectId, this.userChoicesActiveFiltersClfLedger.crossConnectId)
      && this.matches(row.serviceType, this.userChoicesActiveFiltersClfLedger.serviceType)
      && this.matchesDateRange(row.connectionDate, range?.[0] ?? null, range?.[1] ?? null);
  }

  private matches(value: string | null | undefined, filterValue: string | null): boolean {
    if (!filterValue || filterValue === '') {
      return true;
    }
    if (!value) {
      return false;
    }
    return value.toLowerCase().includes(filterValue.toLowerCase());
  }

  private matchesAny(value: string | null | undefined, selectedValues: string[] | null): boolean {
    if (!selectedValues || selectedValues.length === 0) {
      return true;
    }
    if (!value) {
      return false;
    }
    return selectedValues.includes(value);
  }

  private matchesDateRange(value: string | null | undefined, from: string | null, to: string | null): boolean {
    if (!from && !to) {
      return true;
    }
    const time = this.toDayStart(value);
    if (time === null) {
      return false;
    }
    const fromTime = this.toDayStart(from);
    if (fromTime !== null && time < fromTime) {
      return false;
    }
    const toTime = this.toDayStart(to);
    return !(toTime !== null && time > toTime);
  }

  private toDayStart(value: string | null | undefined): number | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  public isRemoved(row: ClfLedgerRow): boolean {
    return !!row.removalDate;
  }

  public isIntraColocation(row: ClfLedgerRow): boolean {
    const injection = (row.injectionDemarcationPoint || '').trim();
    const reception = (row.receptionDemarcationPoint || '').trim();
    return injection !== '' && injection === reception;
  }

  public displayBandwidth(value: number | null): string {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '';
    }
    return new Intl.NumberFormat('en-US').format(value);
  }

  public displayMonthlyFee(row: ClfLedgerRow): string {
    const amount = row.monthlyFee;
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
      return '';
    }
    if (row.monthlyFeeCurrency) {
      const symbol = this.currencySymbols[row.monthlyFeeCurrency.toUpperCase()];
      if (symbol) {
        return `${symbol}${this.formatNumber(amount)}`;
      }
      return `${this.formatNumber(amount)} ${row.monthlyFeeCurrency}`;
    }
    return this.formatNumber(amount);
  }

  private formatNumber(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  public displayDate(value: string | null): string {
    if (!value) {
      return '';
    }
    const formatted = this.datePipe.transform(value, 'M/d/yyyy');
    return formatted || '';
  }
}
